import WhatsApp from '@mui/icons-material/WhatsApp';
import Button, { type ButtonProps } from '@mui/material/Button';
import { type MouseEventHandler, type ReactNode } from 'react';

import { type WhatsAppContactId } from '@/config/whatsappContacts';
import { useWhatsAppLink } from '@/hooks/useWhatsAppLink';
import { whatsapp as green } from '@/theme/tokens';

import { VisuallyHidden } from './VisuallyHidden';

/**
 * How loud the button is.
 *
 * `solid` is the default and the only one that is safe anywhere: the brand
 * green carries dark text at 9.9:1, so the button supplies its own contrast
 * and does not care what it is placed on.
 *
 * `outline` steps the button down to a rank below a filled primary, for the
 * one place that offers both at once. It is **only** legible on a dark
 * surface: the green measures roughly 6.6:1 as text on the opening band's
 * scrim but around 2:1 on the site's light surfaces, which is why it is a
 * named option rather than a `variant` callers can reach for freely. Anything
 * on a light band keeps `solid`.
 */
export type WhatsAppEmphasis = 'solid' | 'outline';

type Props = {
  /** Prefilled message, written for the context the button sits in (§28). */
  message: string;
  /**
   * A seat other than the page's own, for a button that is not about the page
   * it sits on. Left out almost everywhere: the number comes from the route,
   * the same way the message does. See `config/whatsappContacts.ts`.
   */
  contactId?: WhatsAppContactId;
  children?: ReactNode;
  size?: NonNullable<ButtonProps['size']>;
  fullWidth?: boolean;
  emphasis?: WhatsAppEmphasis;
  sx?: NonNullable<ButtonProps['sx']>;
  /**
   * Called as the visitor leaves for WhatsApp, for the rare caller that has to
   * know they went — the scroll tip retires itself once it has been acted on.
   * It reports, it does not navigate: the href is still what opens WhatsApp,
   * so a handler that throws cannot strand the visitor on the page.
   *
   * Typed for an anchor and not for `ButtonProps['onClick']`, which is a
   * button handler: this control always renders as an `<a>`, because it always
   * has an `href`.
   */
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

const outlineSx = {
  color: green.main,
  borderColor: 'rgba(37,211,102,0.55)',
  '&:hover': { borderColor: green.main, bgcolor: 'rgba(37,211,102,0.12)' },
};

/**
 * The WhatsApp role of AppButton, as an external link. Renders nothing when no
 * WhatsApp number is configured.
 *
 * Which number it opens is the route's business, not the caller's: the hook
 * resolves the seat for the page this button is rendered on (see
 * `config/whatsappContacts.ts`). That is why per-page numbers touched none of
 * the pages — a button that was already saying the right thing now also
 * reaches the right person.
 */
export function WhatsAppButton({
  message,
  contactId,
  children = 'WhatsApp Us',
  emphasis = 'solid',
  sx,
  ...rest
}: Props) {
  const href = useWhatsAppLink({ message, contactId });
  if (!href) return null;

  const outlined = emphasis === 'outline';
  // Spread rather than nest, for the same reason AppButton does: MUI's `sx`
  // array takes style objects, not arrays, so an incoming array has to be
  // flattened into this one.
  const mergedSx = outlined ? [outlineSx, ...(Array.isArray(sx) ? sx : [sx ?? false])] : sx;

  return (
    <Button
      variant={outlined ? 'outlined' : 'contained'}
      {...(outlined ? {} : { color: 'whatsapp' as const })}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<WhatsApp />}
      {...rest}
      {...(mergedSx ? { sx: mergedSx } : {})}
    >
      {children}
      <VisuallyHidden> (opens WhatsApp in a new tab)</VisuallyHidden>
    </Button>
  );
}
